// components/Billing/BookingTab.jsx
import React, { useEffect, useState } from "react";
import { Box, Stack, TextField, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import {
  listAllHotelInoices,
  resetAllHotelInvoices,
} from "../../../store/actions"; // 1. Cleaned up imports
import { useSelector, useDispatch } from "react-redux";
import { useTheme } from "@mui/material/styles"; // 2. Corrected useTheme import
import { tokens } from "../../../theme";
import moment from "moment";

// Re-usable components you likely have
import { PaginationFooter, tableStyle } from "../../../components";

// Helper function for formatting dates
const formatUnixDate = (timestamp) => {
  if (!timestamp) return "N/A";
  return moment.unix(timestamp).format("DD MMM YYYY");
};

// Helper function for getting unique room numbers
const getUniqueRoomNumbers = (room_charges = []) => {
  if (!room_charges.length) return "-";

  const validNumbers = room_charges
    .map((c) => c.roomNumber)
    .filter((num) =>
      typeof num === "string" ? num.trim() !== "" : num != null
    );

  if (validNumbers.length === 0) return "-";

  const uniqueNumbers = [...new Set(validNumbers)];
  return uniqueNumbers.join(", ");
};

// ✅ Columns definition using the helper functions for cleaner code
const columns = [
  { field: "inv_no", headerName: "Invoice No.", flex: 1.2 },
  { field: "booking_id", headerName: "Booking ID", flex: 1.2 },
  {
    field: "room_number",
    headerName: "Room(s)",
    flex: 0.8,
    valueGetter: (params) => getUniqueRoomNumbers(params.row.room_charges),
  },
  {
    field: "amount",
    headerName: "Amount",
    flex: 0.75,
    type: "number",
    valueGetter: (params) => params.row.total || 0,
  },
  {
    field: "checkin_date",
    headerName: "Check-in",
    flex: 1,
    valueGetter: (params) => formatUnixDate(params.value),
  },
  {
    field: "checkout_date",
    headerName: "Check-out",
    flex: 1,
    valueGetter: (params) => formatUnixDate(params.value),
  },
];

const BookingTab = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [bookingId, setBookingId] = useState("");
  const [roomNumber, setRoomNumber] = useState("");
  const [checkinDate, setCheckinDate] = useState(null); // Use null for DatePicker
  const [checkoutDate, setCheckoutDate] = useState(null);

const { hotelInvoicesList, pageNumber, pageSize, count, isLoading } =
  useSelector(
    ({
      loading,
      admin: {
        hotelInvoice = {}, // default empty object
      } = {}, // default empty object if admin is undefined
    }) => ({
      isLoading: loading,
      hotelInvoicesList: hotelInvoice.list || [],
      pageNumber: hotelInvoice.pageNumber || 1,
      pageSize: hotelInvoice.pageSize || 20,
      count: hotelInvoice.totalCount || 0,
    })
  );


  useEffect(() => {
    dispatch(resetAllHotelInvoices()); // Clear existing data
    fetchMoreData({ pageNumber: 1 }); // ✅ FIXED: Consistent key name
  }, [dispatch]);

  const fetchMoreData = (page) => {
    const filters = {
      page_number: page?.pageNumber || 1,
      page_size: page?.pageSize || pageSize,
      booking_id: bookingId,
      room_number: roomNumber,
      checkin_date: checkinDate
        ? moment(checkinDate, "YYYY-MM-DD").startOf("day").unix()
        : null,
      checkout_date: checkoutDate
        ? moment(checkoutDate, "YYYY-MM-DD").endOf("day").unix()
        : null,
    };

    Object.keys(filters).forEach((key) => !filters[key] && delete filters[key]);

    dispatch(listAllHotelInoices(filters));
  };

  const handleFilter = () => {
    dispatch(resetAllHotelInvoices());
    fetchMoreData({ pageNumber: 1 });
  };

  const handleViewBooking = (invoice) => {
    navigate(`/billing/bookings/${invoice._id}`);
  };

  const CustomFooter = () => (
    <PaginationFooter
      count={count}
      pageSize={pageSize}
      pageNumber={pageNumber}
      fetchMoreData={(page) => fetchMoreData(page)}
    />
  );

  return (
    <Box m="20px">
      {/* Filter Bar */}
      <Stack
        direction="row"
        spacing={1}
        my={2}
        alignItems="center"
        flexWrap="wrap"
      >
        <TextField
          label="Booking ID"
          size="small"
          value={bookingId}
          onChange={(e) => setBookingId(e.target.value)}
        />
        <TextField
          label="Room Number"
          size="small"
          value={roomNumber}
          onChange={(e) => setRoomNumber(e.target.value)}
        />
        <TextField
          label="Check-in after"
          type="date"
          size="small"
          value={checkinDate}
          onChange={(e) => setCheckinDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            '& input[type="date"]::-webkit-calendar-picker-indicator': {
              filter:
                theme.palette.mode === "dark"
                  ? "invert(1)" // white in dark mode
                  : "invert(0)", // normal in light mode
            },
          }}
        />
        <TextField
          label="Check-out before"
          type="date"
          size="small"
          value={checkoutDate}
          onChange={(e) => setCheckoutDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          InputProps={{ inputProps: { min: checkinDate } }}
          sx={{
            '& input[type="date"]::-webkit-calendar-picker-indicator': {
              filter:
                theme.palette.mode === "dark"
                  ? "invert(1)" // white in dark mode
                  : "invert(0)", // normal in light mode
            },
          }}
        />
        <Button variant="contained" onClick={handleFilter}>
          Search
        </Button>
      </Stack>

      <Box
        sx={{
          height: "68vh",
          width: "100%",
          ...tableStyle(colors),
        }}
      >
        <DataGrid
          loading={isLoading}
          rows={hotelInvoicesList}
          columns={columns}
          components={{ Footer: CustomFooter }}
          getRowId={(row) => row.booking_id}
          onRowClick={(params) => handleViewBooking(params.row)}
          sx={{
            "& .MuiDataGrid-row:hover": { cursor: "pointer" },
          }}
        />
      </Box>
    </Box>
  );
};

export default BookingTab;
